/**
 * YAML Preview Step (Wizard step 6, final step).
 *
 * A read-only review of the assembled YAML tool config. The wizard state is fed
 * through the (peer-owned) `assembleYaml` serializer, which returns the rendered
 * YAML plus any non-blocking assembly warnings. The user reviews the config,
 * then downloads it or copies it to the clipboard to submit to Bluey.
 *
 * The one network touch is the ADVISORY "Run config tester" action, which POSTs
 * the assembled YAML to the backend schema validator and shows approved / a
 * field-level error list. It never gates Finish and never dispatches — the
 * outcome lives in local transient state. `assembleYaml` is memoized on `config`
 * so the YAML is only re-rendered when the wizard state actually changes.
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Button,
  Box,
  Alert,
  StatusIndicator,
  Select,
} from '@amzn/awsui-components-react';
import * as tokens from '@amzn/awsui-design-tokens';

import { OnboardingApiError, testConfig } from '../onboardingApi';
import { OnboardingAction, SECRET_ENV_OPTIONS, SecretEnv } from '../onboardingReducer';
import { ConfigTestResponse, OnboardingConfig } from '../types';
import { assembleYaml } from '../yamlAssembler';
import { SECRET_ARN_PATTERN } from '../onboardingValidators';
import CatalogMetadataSection from './CatalogMetadataSection';

export interface YamlPreviewStepProps {
  config: OnboardingConfig;
  /**
   * Reducer dispatch, for the catalog section's editable schema field. The
   * catalog section only renders when this is supplied (standalone usage — e.g.
   * a unit test rendering just the YAML preview — omits it).
   */
  dispatch?: React.Dispatch<OnboardingAction>;
  /** Whether the backend is reachable; gates the catalog write action. */
  backendAvailable?: boolean;
}

const YamlPreviewStep: React.FC<YamlPreviewStepProps> = ({
  config,
  dispatch,
  backendAvailable = false,
}) => {
  const result = useMemo(() => assembleYaml(config), [config]);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Advisory config-tester state — transient (never dispatched, never gates
  // Finish). result and error are mutually exclusive outcomes of the last run.
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<ConfigTestResponse | null>(null);
  // Which environment's secret ARN to connect with for the (optional) live
  // column check. Mirrors CatalogMetadataSection's writeEnv; defaults to 'test'.
  const [testEnv, setTestEnv] = useState<SecretEnv>('test');
  // Latest assembled YAML, tracked in a ref so an in-flight tester request can
  // detect that the config changed before it resolved (see handleRunTester).
  const latestYaml = useRef('');

  // Clear the transient "Copied" timer on unmount so setCopied isn't called
  // after the component is gone.
  useEffect(
    () => () => {
      if (copyTimer.current) {
        clearTimeout(copyTimer.current);
      }
    },
    [],
  );

  const yaml = result.yaml || '';

  // Discard a prior config-tester outcome whenever the assembled YAML changes,
  // so a stale "approved" can't misrepresent edited config. The catalog section
  // on this same step can dispatch SET_CATALOG_SCHEMA (re-memoizing `yaml`)
  // without unmounting this component, which would otherwise leave a green
  // result describing different YAML. Keyed on `yaml`, which is stable until an
  // edit — so this does NOT clobber the result right after a successful run.
  useEffect(() => {
    latestYaml.current = yaml;
    setTestResult(null);
    setTestError(null);
  }, [yaml]);

  const fileNameBase = config.toolName || 'tool';
  const fileName = `${fileNameBase}_config.yaml`;
  const lineCount = yaml.split('\n').length;

  const handleDownload = () => {
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(yaml);
    setCopied(true);
    if (copyTimer.current) {
      clearTimeout(copyTimer.current);
    }
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  // The selected environment's secret ARN, if it is a well-formed Secrets Manager
  // ARN. When present it is sent with the tester request to enable the live column
  // check; when the field is blank/invalid we simply fall back to the schema-only
  // check (no error), so an incomplete DB config never blocks the tester.
  const testerSecretArn = config.database.secretArns[testEnv].trim();
  const canLiveCheck = SECRET_ARN_PATTERN.test(testerSecretArn);
  // The dropdown label ("Live (prod)"), not the raw env key ("live"), for the hint.
  const testEnvLabel = SECRET_ENV_OPTIONS.find((o) => o.value === testEnv)?.label ?? testEnv;

  const handleRunTester = async () => {
    if (!yaml) return;
    const testedYaml = yaml;

    setTesting(true);
    setTestError(null);
    setTestResult(null);

    try {
      // A schema-validation failure (and a live column mismatch) comes back as
      // HTTP 200 with approved:false, so only a network/other error reaches this
      // catch. Include the secret ARN only when it is valid — otherwise this is
      // the original schema-only request.
      const response = await testConfig(
        canLiveCheck ? { config_yaml: testedYaml, secret_arn: testerSecretArn } : { config_yaml: testedYaml },
      );
      // If the config changed while this request was in flight (e.g. editing the
      // catalog schema on this same step), the [yaml] effect already cleared the
      // result; writing this now-stale response would re-introduce it. Drop it.
      // (An env switch cannot race this: the env Select is disabled while
      // `testing`, so the request's env can't change before it resolves.)
      if (testedYaml !== latestYaml.current) return;
      setTestResult(response);
    } catch (err) {
      if (testedYaml !== latestYaml.current) return;
      setTestError(err instanceof OnboardingApiError ? err.message : 'Config test failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {result.warnings.length > 0 && (
        <Alert type="warning">{result.warnings.join(' • ')}</Alert>
      )}

      <Container
        header={
          <Header
            variant="h2"
            description="This is the final assembled YAML tool config, rendered from everything you entered in the wizard. Download it or copy it to submit to Bluey."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="download" onClick={handleDownload}>
                  Download YAML
                </Button>
                <Button iconName="copy" onClick={handleCopy}>
                  Copy
                </Button>
                {copied && <StatusIndicator type="success">Copied</StatusIndicator>}
                {/* Environment whose secret ARN the live column check connects
                    with. Picking one whose ARN is set upgrades the tester from
                    schema-only to also validating every metric's columns against
                    Redshift; a blank/invalid ARN silently falls back to schema-only. */}
                <Select
                  selectedOption={SECRET_ENV_OPTIONS.find((o) => o.value === testEnv) ?? null}
                  onChange={({ detail }) => {
                    setTestEnv((detail.selectedOption.value as SecretEnv) ?? 'test');
                    // Discard any prior outcome: it was computed against a
                    // DIFFERENT environment's cluster, so leaving it up would
                    // attribute a test-env pass (or failure) to the newly-selected
                    // env — the hint text below would then claim columns were
                    // checked against a cluster we never queried. Mirrors
                    // CatalogMetadataSection's env onChange.
                    setTestResult(null);
                    setTestError(null);
                  }}
                  options={SECRET_ENV_OPTIONS}
                  // Locked while a test is in flight so the env can't change under
                  // an in-flight request: the response validates ONE env's cluster,
                  // and the YAML-based staleness guard in handleRunTester does not
                  // catch an env switch (the YAML is unchanged), so a switched-env
                  // result would otherwise be shown attributed to the new env.
                  disabled={!backendAvailable || testing}
                  ariaLabel="Config tester environment"
                />
                {/* Advisory only: the config tester needs the backend, so it is
                    disabled when the service endpoint was not substituted. */}
                <Button
                  loading={testing}
                  disabled={!backendAvailable}
                  onClick={handleRunTester}
                >
                  Run config tester
                </Button>
              </SpaceBetween>
            }
          >
            Review &amp; Generate
          </Header>
        }
      >
        <SpaceBetween size="m">
          <pre
            style={{
              whiteSpace: 'pre',
              overflow: 'auto',
              maxHeight: 480,
              fontFamily: tokens.fontFamilyMonospace,
              fontSize: 12,
              padding: 12,
              margin: 0,
              border: `1px solid ${tokens.colorBorderDividerDefault}`,
              borderRadius: 4,
              // Theme-aware background + text so the YAML stays readable in both
              // light and dark modes (hardcoded near-white left white-on-white in
              // dark mode, where the inherited body text color is light).
              background: tokens.colorBackgroundContainerContent,
              color: tokens.colorTextBodyDefault,
            }}
          >
            {yaml}
          </pre>
          <Box variant="small">
            {lineCount} lines · {fileName}
          </Box>

          {/* Tell the user whether the tester will do the live column check. It
              needs a valid secret ARN for the selected environment; without one it
              still runs, but only validates the config's shape. */}
          {backendAvailable && (
            <Box variant="small" color="text-body-secondary">
              {canLiveCheck
                ? `The config tester validates the schema and checks each base metric's columns against Redshift using the ${testEnvLabel} secret.`
                : 'The config tester validates the config schema. Set a valid secret ARN for the selected environment to also check metric columns against Redshift.'}
            </Box>
          )}

          {/* Advisory config-tester outcome. This validates the config against
              Bluey's schema; it does NOT block the Finish button. The backend
              is trusted but not guaranteed to send well-formed shapes, so both
              `errors` and `warnings` are guarded against being missing/null —
              an unguarded `.map` on undefined would throw during render, and
              AppInitWrapper's error boundary re-throws into a whole-site white
              screen. */}
          {testResult?.approved &&
            (() => {
              const warnings = testResult.warnings ?? [];
              const parsedAs = testResult.toolName
                ? ` as ${testResult.toolName}${testResult.toolType ? ` (${testResult.toolType})` : ''}`
                : '';
              return (
                <SpaceBetween size="xs">
                  <StatusIndicator type="success">
                    Config approved{parsedAs} — valid Bluey tool config
                  </StatusIndicator>
                  {warnings.length > 0 && (
                    <Alert type="warning" header="Validator warnings">
                      <SpaceBetween size="xs">
                        <Box variant="p">
                          Advisory only — the config was approved. Consider addressing these before
                          submitting to Bluey:
                        </Box>
                        <ul>
                          {warnings.map((warning, index) => (
                            <li key={`${index}: ${warning}`}>{warning}</li>
                          ))}
                        </ul>
                      </SpaceBetween>
                    </Alert>
                  )}
                </SpaceBetween>
              );
            })()}

          {testResult &&
            !testResult.approved &&
            (() => {
              const fieldErrors = testResult.errors ?? [];
              return (
                <Alert type="error" header="Config validation failed">
                  <SpaceBetween size="xs">
                    {fieldErrors.length > 0 ? (
                      <>
                        <Box variant="p">
                          Advisory only — you can still finish onboarding. Fix these fields before
                          submitting the config to Bluey:
                        </Box>
                        <ul>
                          {fieldErrors.map((fieldError, index) => (
                            <li key={`${index}: ${fieldError.loc}`}>
                              <Box variant="code">{fieldError.loc}</Box>: {fieldError.message}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <Box variant="p">
                        Advisory only — you can still finish onboarding. Config was rejected but no
                        specific field errors were returned.
                      </Box>
                    )}
                  </SpaceBetween>
                </Alert>
              );
            })()}

          {testError && (
            <Alert type="error" dismissible onDismiss={() => setTestError(null)}>
              {testError}
            </Alert>
          )}
        </SpaceBetween>
      </Container>

      {/* The YAML only NAMES the metric catalog tables; the stored procedure
          resolves metrics against the tables themselves. This section creates
          them (or exports them as CSV), so it belongs with the final review. */}
      {dispatch && (
        <CatalogMetadataSection
          config={config}
          dispatch={dispatch}
          backendAvailable={backendAvailable}
        />
      )}
    </SpaceBetween>
  );
};

export default YamlPreviewStep;
